/**
 * SignupPage — email → OTP → passkey choice → handoff. Accounts are approved
 * immediately on OTP verification; no pending-approval wait state exists here.
 * (The "account approved" magic-link entry point below still applies to the
 * separate admin-initiated org-management approve flow, which is unrelated
 * to this signup path.)
 *
 * Passkey step is a fork: "Op mijn telefoon" (recommended, registers via a
 * QR hand-off so the credential lives on the phone) or "Op dit toestel"
 * (current single-device WebAuthn — flagged as not recommended on shared PCs).
 */

import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import QRCode from "qrcode";
import { API_URL } from "@/lib/env";
import { carmenAuth } from "@/lib/carmenAuth";
import { registerPasskey } from "@/lib/passkey";
import { initHandoff, subscribeHandoff, type HandoffSession } from "@/lib/handoff";
import { isMobileDevice } from "@/lib/device";
import { AltchaWidget } from "@/components/AltchaWidget";

type Step = "email" | "otp" | "choose" | "handoff" | "this-device" | "handoff-done";

type OtpVerifyResponse = { email: string; magic_link_token: string };

const TERMS_VERSION = "2026-08-06";
const PRIVACY_VERSION = "2026-08-06";

const PANEL_CLASS =
  "rounded-[28px] border border-[rgba(10,52,96,0.10)] bg-white/92 p-5 shadow-[0_20px_54px_rgba(10,52,96,0.10)] backdrop-blur-xl sm:p-6";
const FIELD_CLASS =
  "mt-1.5 w-full rounded-[16px] border border-[rgba(10,52,96,0.16)] bg-white px-4 py-2 text-[15px] text-[#111111] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] outline-none transition-all duration-200 placeholder:text-[rgba(125,156,180,0.95)] focus:border-[rgba(76,199,239,0.52)] focus:ring-4 focus:ring-[rgba(76,199,239,0.12)]";
const REQUIRED_FIELD_CLASS =
  "mt-1.5 w-full rounded-[16px] border border-[rgba(76,199,239,0.72)] bg-white px-4 py-2 text-[15px] text-[#111111] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] outline-none transition-all duration-200 placeholder:text-[rgba(125,156,180,0.95)] focus:border-[rgba(10,52,96,0.72)] focus:ring-4 focus:ring-[rgba(76,199,239,0.12)]";
const PRIMARY_BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-navy)] px-5 py-3 text-[15px] font-semibold text-white shadow-[0_14px_30px_rgba(10,52,96,0.18)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[var(--cs-navy-strong)] disabled:cursor-not-allowed disabled:opacity-50";
const SECONDARY_BUTTON_CLASS =
  "w-full rounded-full border border-[rgba(10,52,96,0.18)] bg-white px-5 py-3.5 text-[15px] font-semibold text-[var(--cs-navy)] shadow-[0_12px_28px_rgba(10,52,96,0.06)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[rgba(247,251,255,0.9)] disabled:cursor-not-allowed disabled:opacity-50";

function FieldLabel({
  htmlFor,
  label,
}: {
  htmlFor: string;
  label: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="flex items-center justify-between gap-3 text-[14px] font-medium text-[var(--cs-navy)]"
    >
      <span>{label}</span>
    </label>
  );
}

function Card({
  title,
  titleClassName = "text-[#111111]",
  children,
}: {
  title: string;
  titleClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={PANEL_CLASS}>
      <div className="mb-4 text-center">
        <h1 className={`text-[30px] font-semibold tracking-[-0.04em] ${titleClassName}`}>{title}</h1>
      </div>
      {children}
    </div>
  );
}

export function SignupPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // Magic link from the "account approved" email lands here with ?token_hash=…
  // — establish the session and jump straight to passkey enrollment.
  const approvalToken = params.get("token_hash") ?? "";

  const [step, setStep] = useState<Step>("email");
  const [verifyingLink, setVerifyingLink] = useState(Boolean(approvalToken));
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [altchaPayload, setAltchaPayload] = useState<string | null>(null);
  // Bumped on a failed send to remount AltchaWidget so a fresh challenge is
  // required (the widget has no public reset() in the React wrapper).
  const [altchaAttempt, setAltchaAttempt] = useState(0);
  // Token verifyOtp() returns — used to register directly rather than relying
  // on getSession() at click-time, which can return null in some browsers
  // (e.g. email in-app webviews) and leave the registration call unauthed.
  const accessTokenRef = useRef<string>("");

  useEffect(() => {
    if (!approvalToken) return;
    let cancelled = false;
    void (async () => {
      const { session, error: exchangeError } = await carmenAuth.exchangeToken({
        token_hash: approvalToken,
      });
      if (cancelled) return;
      // Drop the token from the URL so a refresh doesn't retry a spent link.
      setParams({}, { replace: true });
      if (exchangeError) {
        setError(
          /expired|invalid/i.test(exchangeError.message)
            ? "Deze link is verlopen of al gebruikt. Log opnieuw in met uw e-mailadres om door te gaan."
            : exchangeError.message,
        );
        setStep("email");
      } else {
        accessTokenRef.current = session?.access_token ?? "";
        setStep(isMobileDevice() ? "this-device" : "choose");
      }
      setVerifyingLink(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [approvalToken, setParams]);

  const handleEmailSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError("");
      setLoading(true);
      try {
        const resp = await fetch(`${API_URL}/api/auth/otp/send`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: email.trim(), altcha: altchaPayload }),
        });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as { detail?: string };
          throw new Error(body.detail ?? `Fout ${resp.status}`);
        }
        setStep("otp");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Onbekende fout");
        setAltchaPayload(null);
        setAltchaAttempt((a) => a + 1);
      } finally {
        setLoading(false);
      }
    },
    [altchaPayload, email],
  );

  const handleOtpSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError("");
      setLoading(true);
      try {
        const resp = await fetch(`${API_URL}/api/auth/otp/verify`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            code: otp.trim(),
            full_name: fullName.trim(),
            role: role.trim(),
            workplace: workplace.trim(),
            legal_accepted: legalAccepted,
            terms_version: TERMS_VERSION,
            privacy_version: PRIVACY_VERSION,
          }),
        });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as { detail?: string };
          throw new Error(body.detail ?? "Verificatie mislukt");
        }
        const data = (await resp.json()) as OtpVerifyResponse;
        const { session, error: exchangeError } = await carmenAuth.exchangeToken({
          token_hash: data.magic_link_token,
        });
        if (exchangeError) throw new Error(exchangeError.message);
        accessTokenRef.current = session?.access_token ?? "";
        setStep(isMobileDevice() ? "this-device" : "choose");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verificatie mislukt");
      } finally {
        setLoading(false);
      }
    },
    [email, fullName, legalAccepted, otp, role, workplace],
  );

  const handleThisDevice = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      await registerPasskey("Primary device", {
        accessToken: accessTokenRef.current || undefined,
      });
      nav("/download", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey registratie mislukt");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  if (verifyingLink) {
    return (
      <div className="cs-marketing -mt-6 min-h-[calc(100vh-9rem)] bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-0 py-2 sm:-mt-8 sm:py-3">
        <div className="mx-auto w-full max-w-md space-y-6">
          <Card title="Account activeren">
            <p className="text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Bezig met verifiëren…
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="cs-marketing -mt-6 min-h-[calc(100vh-9rem)] bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-0 py-2 sm:-mt-8 sm:py-3">
      <div className="mx-auto w-full max-w-md space-y-6">
        {step === "email" ? (
          <Card title="Registreer" titleClassName="text-[var(--cs-navy)]">
            {error ? (
              <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
                {error}
              </div>
            ) : null}
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div>
                <FieldLabel htmlFor="email" label="E-mailadres" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="info@askcarmen.eu"
                  required
                  className={REQUIRED_FIELD_CLASS}
                />
              </div>
              <div>
                <FieldLabel htmlFor="full-name" label="Naam" />
                <input
                  id="full-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  placeholder="Carmen"
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <FieldLabel htmlFor="role" label="Functie/rol" />
                <input
                  id="role"
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  autoComplete="organization-title"
                  placeholder="Cardioloog"
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <FieldLabel htmlFor="workplace" label="Werkplaats" />
                <input
                  id="workplace"
                  type="text"
                  value={workplace}
                  onChange={(e) => setWorkplace(e.target.value)}
                  autoComplete="organization"
                  placeholder="UZ Gent"
                  className={FIELD_CLASS}
                />
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-[16px] px-1 py-1 text-[13px] leading-5 text-[#536274]">
                <input
                  type="checkbox"
                  checked={legalAccepted}
                  onChange={(event) => setLegalAccepted(event.target.checked)}
                  required
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--cs-navy)]"
                />
                <span>
                  Ik handel beroepsmatig en ben, als ik namens een praktijk of organisatie
                  handel, bevoegd die te verbinden. Ik aanvaard de{" "}
                  <Link to="/terms" className="font-medium text-[var(--cs-navy)] underline underline-offset-2">
                    Gebruiksvoorwaarden
                  </Link>{" "}
                  en heb het{" "}
                  <Link to="/privacy" className="font-medium text-[var(--cs-navy)] underline underline-offset-2">
                    Privacybeleid
                  </Link>{" "}
                  gelezen.
                </span>
              </label>
              <AltchaWidget
                key={altchaAttempt}
                challengeUrl={`${API_URL}/api/auth/otp/challenge`}
                onPayload={setAltchaPayload}
              />
              <button
                type="submit"
                disabled={loading || !legalAccepted || !altchaPayload}
                className={PRIMARY_BUTTON_CLASS}
              >
                {loading ? "Bezig…" : "Doorgaan"}
              </button>
            </form>
            <div className="mt-4 text-center text-sm text-[rgba(95,131,160,0.96)]">
              Al een account?{" "}
              <a
                href="/login"
                className="text-[var(--cs-navy)] transition-colors hover:text-[var(--cs-navy-strong)]"
              >
                Inloggen
              </a>
            </div>
          </Card>
        ) : step === "otp" ? (
          <Card title="Code controleren">
            <p className="mb-5 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Er is een code verzonden naar <strong>{email}</strong>
            </p>
            {error ? (
              <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
                {error}
              </div>
            ) : null}
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="otp"
                  className="block text-[15px] font-medium text-[var(--cs-navy)]"
                >
                  Verificatiecode
                </label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="123456"
                  className={`${FIELD_CLASS} text-center font-mono text-2xl tracking-[0.45em]`}
                />
              </div>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className={PRIMARY_BUTTON_CLASS}
              >
                {loading ? "Verifiëren…" : "Verifiëren"}
              </button>
            </form>
          </Card>
        ) : step === "choose" ? (
          <Card title="Passkey instellen">
            <div className="mb-6 space-y-2 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              <p>Uw account en medische gegevens worden beschermd met een persoonlijke passkey.</p>
              <p>Waar wilt u uw passkey opslaan?</p>
            </div>
            {error ? (
              <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
                {error}
              </div>
            ) : null}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setStep("handoff")}
                disabled={loading}
                className={PRIMARY_BUTTON_CLASS}
              >
                Op mijn telefoon (aanbevolen)
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => void handleThisDevice()}
                  disabled={loading}
                  className={SECONDARY_BUTTON_CLASS}
                >
                  {loading ? "Bezig…" : "Op dit toestel"}
                </button>
                <p className="mt-2 text-center text-xs text-[rgba(125,156,180,0.96)]">
                  Niet aanbevolen op een gedeelde of werk-PC
                </p>
              </div>
            </div>
          </Card>
        ) : step === "this-device" ? (
          <Card title="Passkey instellen">
            <p className="mb-6 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Uw account en medische gegevens worden beschermd met een persoonlijke passkey.
              Maak de passkey aan op dit toestel met uw vingerafdruk, gezichts-ID of pincode.
            </p>
            {error ? (
              <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
                {error}
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => void handleThisDevice()}
              disabled={loading}
              className={PRIMARY_BUTTON_CLASS}
            >
              {loading ? "Bezig…" : "Passkey aanmaken"}
            </button>
          </Card>
        ) : step === "handoff" ? (
          <SignupHandoffPanel
            email={email}
            onDone={() => setStep("handoff-done")}
            onCancel={() => setStep("choose")}
          />
        ) : step === "handoff-done" ? (
          <Card title="Klaar!">
            <p className="mb-6 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Uw passkey is geregistreerd op uw telefoon. U kunt Carmen nu downloaden.
            </p>
            <button
              type="button"
              onClick={() => nav("/download", { replace: true })}
              className={PRIMARY_BUTTON_CLASS}
            >
              Naar downloads
            </button>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function SignupHandoffPanel({
  email,
  onDone,
  onCancel,
}: {
  email: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [session, setSession] = useState<HandoffSession | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(600);
  const [expired, setExpired] = useState(false);
  const [copied, setCopied] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const navTriggered = useRef(false);
  // Hold onDone in a ref so `start` stays stable across parent re-renders
  // (parent passes a fresh arrow on each render). Without this, tab-switch
  // visibility events cause the parent to re-render, `start` to be recreated,
  // useEffect to re-fire, and the QR + countdown to reset.
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const start = useCallback(async () => {
    setError("");
    setExpired(false);
    setCountdown(600);
    setQrDataUrl("");
    cleanupRef.current?.();
    cleanupRef.current = null;
    navTriggered.current = false;
    try {
      const s = await initHandoff();
      setSession(s);
      const dataUrl = await QRCode.toDataURL(s.handoffUrl, {
        width: 220,
        margin: 2,
        color: { dark: "#0f172a", light: "#ffffff" },
        errorCorrectionLevel: "M",
      });
      setQrDataUrl(dataUrl);
      const cleanup = subscribeHandoff(s, {
        onRegistered: () => {
          if (navTriggered.current) return;
          navTriggered.current = true;
          onDoneRef.current();
        },
        onExpired: () => setExpired(true),
      });
      cleanupRef.current = cleanup;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kon QR-code niet starten");
    }
  }, []);

  useEffect(() => {
    void start();
    return () => cleanupRef.current?.();
  }, [start]);

  useEffect(() => {
    if (!session || expired) return;
    const handle = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(handle);
  }, [session, expired]);

  return (
    <Card title="Scan met uw telefoon">
      <p className="mb-4 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
        Open de camera-app op uw telefoon en scan deze code. Bevestig daarna met uw
        vingerafdruk of gezichts-ID.
      </p>
      <p className="mb-6 text-center text-xs text-[rgba(125,156,180,0.96)]">
        Voor account: <strong>{email}</strong>
      </p>
      {error ? (
        <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
          {error}
        </div>
      ) : null}
      {expired ? (
        <div className="rounded-[28px] border border-[rgba(10,52,96,0.08)] bg-[rgba(247,251,255,0.9)] px-6 py-8 text-center">
          <p className="mb-4 text-sm text-[#B54708]">QR-code verlopen.</p>
          <button type="button" onClick={() => void start()} className={PRIMARY_BUTTON_CLASS}>
            Nieuwe code
          </button>
        </div>
      ) : qrDataUrl ? (
        <div className="rounded-[28px] border border-[rgba(10,52,96,0.08)] bg-[rgba(247,251,255,0.9)] px-6 py-8 text-center">
          <img
            src={qrDataUrl}
            alt="QR code"
            className="mx-auto h-56 w-56 rounded-[20px] bg-white p-3 shadow-[0_18px_38px_rgba(10,52,96,0.08)]"
          />
          <p className="mt-4 text-sm text-[rgba(95,131,160,0.96)]">
            Verloopt in {Math.floor(countdown / 60)}:
            {(countdown % 60).toString().padStart(2, "0")}
          </p>
          <button
            type="button"
            onClick={() => {
              if (!session) return;
              void navigator.clipboard.writeText(session.handoffUrl).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2500);
              });
            }}
            className="mt-3 text-sm text-[var(--cs-navy)] underline transition-colors hover:text-[var(--cs-navy-strong)]"
          >
            {copied ? "Gekopieerd!" : "Kan niet scannen? Kopieer de link"}
          </button>
        </div>
      ) : (
        <div className="text-center text-sm text-[rgba(125,156,180,0.96)]">QR-code genereren…</div>
      )}
      <button
        type="button"
        onClick={onCancel}
        className="mt-5 w-full text-center text-sm text-[var(--cs-navy)] underline transition-colors hover:text-[var(--cs-navy-strong)]"
      >
        ← Andere optie
      </button>
    </Card>
  );
}
